import React from 'react';
import './ArchitectureBackground.css';

const GOLD = '#C9A96E';
const GOLD_LIGHT = '#D4BA85';
const LINE = '#9A9A9A';
const LINE_SOFT = '#B8B8B8';

/* Hexagon point generator */
const hex = (cx, cy, r) => {
  const pts = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i - Math.PI / 2;
    pts.push(`${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`);
  }
  return pts.join(' ');
};

/* =========================================================================
   Shared base: thin diagonal lines, off-canvas outlined circles, gold dots.
   Tuned to be ambient — visible but not competing with foreground content.
   ========================================================================= */
const SharedBase = () => (
  <>
    {/* top-left 45° line cluster */}
    <g className="bg-lines" stroke={LINE} strokeWidth="1" opacity="0.4">
      <line x1="20" y1="280" x2="280" y2="20" />
      <line x1="60" y1="320" x2="320" y2="60" />
      <line x1="100" y1="360" x2="360" y2="100" />
    </g>

    {/* right-middle 45° line cluster */}
    <g className="bg-lines" stroke={LINE} strokeWidth="1" opacity="0.35">
      <line x1="950" y1="320" x2="1170" y2="100" />
      <line x1="990" y1="360" x2="1210" y2="140" />
    </g>

    {/* bottom-right 45° line cluster */}
    <g className="bg-lines" stroke={LINE} strokeWidth="1" opacity="0.32">
      <line x1="780" y1="640" x2="980" y2="440" />
      <line x1="820" y1="680" x2="1020" y2="480" />
      <line x1="860" y1="720" x2="1060" y2="520" />
    </g>

    {/* off-canvas outlined circles — recede further by lowering opacity */}
    <g className="bg-rings" fill="none">
      <circle cx="-40" cy="780" r="230" stroke={GOLD} strokeWidth="1.2" opacity="0.32" />
      <circle cx="-40" cy="780" r="290" stroke={LINE_SOFT} strokeWidth="0.9" opacity="0.28" />
      <circle cx="1390" cy="380" r="155" stroke={GOLD} strokeWidth="1.2" opacity="0.32" />
      <circle cx="1390" cy="380" r="210" stroke={LINE_SOFT} strokeWidth="0.9" opacity="0.22" />
    </g>

    {/* tiny gold dots — pure accents */}
    <g className="bg-dots" fill={GOLD}>
      <circle cx="400" cy="150" r="3.5" opacity="0.55" />
      <circle cx="660" cy="100" r="3"   opacity="0.45" />
      <circle cx="1310" cy="60" r="3"   opacity="0.55" />
      <circle cx="540" cy="700" r="3"   opacity="0.45" />
      <circle cx="950" cy="640" r="3.5" opacity="0.5" />
      <circle cx="1280" cy="660" r="2.5" opacity="0.4" />
    </g>
  </>
);

/* =========================================================================
   Variant 1 — Intelligence (small triangles as accents)
   ========================================================================= */
const IntelligenceBg = ({ active }) => (
  <svg
    className={`layer-bg-svg ${active ? 'is-active' : ''}`}
    viewBox="0 0 1376 768"
    preserveAspectRatio="xMidYMid slice"
    aria-hidden="true"
  >
    <SharedBase />

    <g className="bg-focal">
      {/* small outlined upright triangle (center-left) */}
      <polygon
        points="160,400 200,330 240,400"
        fill="none"
        stroke={GOLD}
        strokeWidth="1.4"
        opacity="0.55"
      />
      {/* tiny filled triangle below */}
      <polygon points="200,480 225,440 250,480" fill={GOLD} opacity="0.55" />
      {/* top-right tiny downward filled triangle */}
      <polygon points="1110,90 1155,90 1132,135" fill={GOLD} opacity="0.5" />
      {/* top-right small outlined triangle */}
      <polygon
        points="1265,185 1295,140 1325,185"
        fill="none"
        stroke={GOLD}
        strokeWidth="1.3"
        opacity="0.55"
      />
      {/* bottom-right tiny filled triangle */}
      <polygon points="1115,560 1142,520 1169,560" fill={GOLD} opacity="0.5" />
    </g>
  </svg>
);

/* =========================================================================
   Variant 2 — Trustworthy (small concentric rings + small hexagons)
   ========================================================================= */
const TrustworthyBg = ({ active }) => (
  <svg
    className={`layer-bg-svg ${active ? 'is-active' : ''}`}
    viewBox="0 0 1376 768"
    preserveAspectRatio="xMidYMid slice"
    aria-hidden="true"
  >
    <SharedBase />

    <g className="bg-focal">
      {/* small concentric rings center-left */}
      <circle cx="200" cy="380" r="26" fill="none" stroke={GOLD} strokeWidth="1.2" opacity="0.4" />
      <circle cx="200" cy="380" r="40" fill="none" stroke={GOLD} strokeWidth="0.9" opacity="0.28" />
      <circle cx="200" cy="380" r="56" fill="none" stroke={LINE_SOFT} strokeWidth="0.8" opacity="0.22" />
      <circle cx="200" cy="380" r="3" fill={GOLD} opacity="0.6" />

      {/* hexagon below the rings */}
      <polygon points={hex(225, 500, 18)} fill={GOLD} opacity="0.32" />

      {/* top-right small filled hexagon */}
      <polygon points={hex(1140, 115, 20)} fill={GOLD} opacity="0.3" />

      {/* top-right small outlined hexagon */}
      <polygon
        points={hex(1290, 165, 17)}
        fill="none"
        stroke={GOLD}
        strokeWidth="1.1"
        opacity="0.38"
      />

      {/* bottom-right small hexagon */}
      <polygon points={hex(1145, 555, 17)} fill={GOLD} opacity="0.3" />
    </g>
  </svg>
);

/* =========================================================================
   Variant 3 — Autonomous Agent
   Smaller, sparser network clustered around the edges; center stays clear.
   ========================================================================= */
const AgentBg = ({ active }) => {
  // Sparser network — clustered top-right + bottom-left corners with a
  // small hub center-left. Avoid crossing through the middle (where
  // foreground text lives).
  const nodes = [
    { x: 200, y: 380, hub: true },   // 0 — center-left hub
    { x: 230, y: 500 },              // 1 — near hex below
    { x: 110, y: 280 },              // 2

    { x: 1140, y: 115, hub: true },  // 3 — top-right hub
    { x: 1240, y: 80  },             // 4
    { x: 1290, y: 175 },             // 5
    { x: 1080, y: 220 },             // 6

    { x: 1145, y: 555 },             // 7 — bottom-right corner
    { x: 1080, y: 620 },             // 8
    { x: 1250, y: 600 },             // 9
  ];

  // Local edges per cluster — keep the center empty
  const edges = [
    [0, 1], [0, 2],
    [3, 4], [3, 5], [3, 6], [4, 6],
    [7, 8], [7, 9],
  ];

  return (
    <svg
      className={`layer-bg-svg ${active ? 'is-active' : ''}`}
      viewBox="0 0 1376 768"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <SharedBase />

      <g className="bg-focal">
        {/* edges (sparse, only within clusters) */}
        <g stroke={GOLD} strokeWidth="0.9" opacity="0.4">
          {edges.map(([a, b], i) => (
            <line key={i} x1={nodes[a].x} y1={nodes[a].y} x2={nodes[b].x} y2={nodes[b].y} />
          ))}
        </g>

        {/* nodes */}
        <g>
          {nodes.map((n, i) => (
            <g key={i}>
              {n.hub && (
                <circle
                  cx={n.x}
                  cy={n.y}
                  r="9"
                  fill="none"
                  stroke={GOLD}
                  strokeWidth="1.2"
                  opacity="0.6"
                />
              )}
              <circle cx={n.x} cy={n.y} r={n.hub ? 4 : 3} fill={GOLD} opacity={n.hub ? 0.75 : 0.55} />
            </g>
          ))}
        </g>

        {/* small dashed orbit ring around bottom-right cluster */}
        <circle
          cx="1145"
          cy="555"
          r="18"
          fill="none"
          stroke={GOLD}
          strokeWidth="0.9"
          strokeDasharray="2 4"
          opacity="0.45"
        />

        {/* one small triangle accent for continuity with variant 1 */}
        <polygon points="1265,185 1295,140 1325,185" fill="none" stroke={GOLD_LIGHT} strokeWidth="1.1" opacity="0.45" />
      </g>
    </svg>
  );
};

/* =========================================================================
   Composer
   ========================================================================= */
const ArchitectureBackground = ({ activeLayer = 0 }) => (
  <div className="architecture-bg-canvas">
    <div className="architecture-bg-base" />
    <IntelligenceBg active={activeLayer === 0} />
    <TrustworthyBg active={activeLayer === 1} />
    <AgentBg active={activeLayer === 2} />
  </div>
);

export default ArchitectureBackground;
